import { z } from "zod/v4";
import {
  CameraSubject,
  ClientApiFeaturePathParams,
  CollectionResponse,
  defineEndpoint,
  defineResource,
  SingleResponse,
  TenantPathParams,
} from "@visioni/a3s-client-api";
import { isoDatetimeToDateString } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";

export const COMPILATION_MAX_RANGE_DAYS = 4;
export const COMPILATION_MAX_RANGE_MS = COMPILATION_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;

/** How many open compilations (waiting, sent or in flight) one camera may hold. */
export const COMPILATION_QUEUE_LIMIT = 5;

const COMPILATION_MAX_RANGE_MESSAGE = `endTime - startTime must be <= ${String(COMPILATION_MAX_RANGE_DAYS)} days`;

// ==================== Path Params ====================

export type RequestCompilationPathParams = ClientApiFeaturePathParams;
export type GetCompilationPathParams = ClientApiFeaturePathParams;
export type ListCompilationsPathParams = TenantPathParams;

// ==================== Compilation ====================

export const CompilationStatus = z.enum(["queued", "processing", "uploading", "ready", "failed"]);
export type CompilationStatus = z.output<typeof CompilationStatus>;

export const RequestCompilationBody = z
  .object({
    startTime: isoDatetimeToDateString,
    endTime: isoDatetimeToDateString,
  })
  // Strictly less-than, matching the edge's own `rangeMs <= 0` rejection in
  // `clip-compilation-edge.ts`. A zero-length range has nothing to compile, so
  // reject it here rather than queue a job that is guaranteed to fail on-device.
  .refine((b) => new Date(b.startTime).getTime() < new Date(b.endTime).getTime(), {
    message: "startTime must be < endTime",
    path: ["endTime"],
  })
  .refine((b) => new Date(b.endTime).getTime() - new Date(b.startTime).getTime() <= COMPILATION_MAX_RANGE_MS, {
    message: COMPILATION_MAX_RANGE_MESSAGE,
    path: ["endTime"],
  });

export type RequestCompilationBody = z.output<typeof RequestCompilationBody>;

export const CompilationSignedUrl = z.object({
  /** Plain streaming URL — no `Content-Disposition` override, so in-app players (`ClipVideoPlayer`) can play it inline. */
  url: z.string(),
  /** Same object, signed with `Content-Disposition: attachment` so a browser download actually saves the file
   * instead of streaming/rendering it — distinct from `url` because the two need opposite disposition.
   */
  downloadUrl: z.string().optional(),
  expiresAt: z.string(),
});

export type CompilationSignedUrl = z.output<typeof CompilationSignedUrl>;

export const CompilationItem = z.object({
  subject: CameraSubject,
  requestId: z.string(),
  status: CompilationStatus,
  startTime: z.string(),
  endTime: z.string(),
  video: CompilationSignedUrl.optional(),
  failureReason: z.string().optional(),
  requestedAt: z.string(),
  updatedAt: z.string(),
  /**
   * How many *other* jobs are open (queued, processing or uploading) on the
   * same camera. Only populated by the list endpoint.
   *
   * Kept for older app builds, which use it to judge whether a long-queued
   * job is dead; newer ones read {@link queuePosition} instead.
   */
  queuedSiblings: z.number().int().nonnegative().optional(),
  /**
   * Where a `queued` job stands in its camera's queue: `0` once its command
   * has been sent to the device, `1..n` while it waits in the cloud behind
   * the jobs ahead of it. Omitted for every other status.
   */
  queuePosition: z.number().int().nonnegative().optional(),
});

export type CompilationItem = z.output<typeof CompilationItem>;

// ==================== Request (POST) ====================

export const RequestCompilationResponse = SingleResponse(CompilationItem);
export type RequestCompilationResponse = z.output<typeof RequestCompilationResponse>;

// ==================== Status (GET single) ====================

export const GetCompilationResponse = SingleResponse(CompilationItem);
export type GetCompilationResponse = z.output<typeof GetCompilationResponse>;

// ==================== Cancel (POST) ====================

export const CancelCompilationResponse = SingleResponse(CompilationItem);
export type CancelCompilationResponse = z.output<typeof CancelCompilationResponse>;

// ==================== List (GET collection) ====================

/**
 * Every filter is applied by the database, so a page is always full unless the
 * results genuinely ran out. Mirrors `a3s-activity`'s list query, including its
 * offset/limit pagination — the two endpoints back near-identical screens.
 */
export const ListCompilationsQueryParams = z.object({
  status: CompilationStatus.optional(),
  siteId: z.string().optional(),
  cameraKey: z.string().optional(),
  /**
   * Inclusive lower bound on the *covered footage window*, not the request
   * time.
   *
   * Must be a full ISO 8601 instant. Both bounds go into the query as
   * `TIMESTAMPTZ`, so anything else either fails the cast in Postgres — a 500
   * for what is really a malformed request — or, for a bare `YYYY-MM-DD`, is
   * silently read as midnight UTC and reintroduces the day-boundary mismatch
   * {@link parseListCompilationsQuery} describes. Rejecting it here answers
   * 400 before the query is built.
   */
  dateFrom: z.iso.datetime({ offset: true }).optional(),
  /** Inclusive upper bound on the covered footage window. Same constraint as {@link dateFrom}. */
  dateTo: z.iso.datetime({ offset: true }).optional(),
  limit: z.string().optional(),
  offset: z.string().optional(),
});

export type ListCompilationsQueryParams = z.output<typeof ListCompilationsQueryParams>;

export interface ParsedListCompilationsQuery {
  status?: CompilationStatus | undefined;
  siteId?: string | undefined;
  cameraKey?: string | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  limit: number;
  offset: number;
}

export const DEFAULT_LIST_LIMIT = 50;
export const MAX_LIST_LIMIT = 200;

/**
 * Normalises the raw query string values.
 *
 * Deliberately does not widen a bare `YYYY-MM-DD` upper bound to the end of
 * that day. Doing so resolves the day in whatever timezone the runtime happens
 * to use — UTC, in Lambda — while the lower bound arrives as an absolute
 * instant in the caller's timezone. The two then describe different days: for a
 * caller east of UTC the upper bound ran on into the next local morning, and
 * for one west of UTC it cut the selected day short. Both bounds are absolute
 * instants, and the caller resolves its own local day before sending them.
 */
export function parseListCompilationsQuery(raw: ListCompilationsQueryParams): ParsedListCompilationsQuery {
  return {
    ...raw,
    limit: Math.min(
      Math.max(parseInt(raw.limit ?? String(DEFAULT_LIST_LIMIT), 10) || DEFAULT_LIST_LIMIT, 1),
      MAX_LIST_LIMIT,
    ),
    offset: Math.max(parseInt(raw.offset ?? "0", 10) || 0, 0),
  };
}

export const ListCompilationsResponse = CollectionResponse(CompilationItem).extend({
  /** Total matching the filters, ignoring limit/offset. */
  totalCount: z.number().int().nonnegative(),
});
export type ListCompilationsResponse = z.output<typeof ListCompilationsResponse>;

// ==================== Resources & Endpoints ====================

export const compilationResource = defineResource({
  feature: FEATURE_NAME,
  resource: "compilation",
});

export const requestCompilationEndpoint = defineEndpoint(compilationResource, {
  method: "POST",
  bodySchema: RequestCompilationBody,
  responseSchema: RequestCompilationResponse,
});

export const compilationByIdResource = defineResource({
  feature: FEATURE_NAME,
  resource: "compilation/{requestId}",
  resourcePathSchema: z.object({ requestId: z.string() }),
});

export const getCompilationEndpoint = defineEndpoint(compilationByIdResource, {
  responseSchema: GetCompilationResponse,
});

export const compilationCancelResource = defineResource({
  feature: FEATURE_NAME,
  resource: "compilation/{requestId}/cancel",
  resourcePathSchema: z.object({ requestId: z.string() }),
});

/** Cancels a compilation still waiting in its camera's queue (`queuePosition` 1 or more). */
export const cancelCompilationEndpoint = defineEndpoint(compilationCancelResource, {
  method: "POST",
  // The path names the job, so there is nothing to send; clients send `{}`.
  bodySchema: z.object({}),
  responseSchema: CancelCompilationResponse,
});

export const compilationListResource = defineResource({
  feature: FEATURE_NAME,
  resource: "compilations",
  scope: "tenant",
});

export const listCompilationsEndpoint = defineEndpoint(compilationListResource, {
  responseSchema: ListCompilationsResponse,
  querySchema: ListCompilationsQueryParams,
});
